import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Wallet, LogOut, Download, FileDown, Trash2, Eye, EyeOff } from "lucide-react";
import { EkycChatButton } from "@/components/EkycChatButton";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// IST helpers
const IST_OPTS = { timeZone: "Asia/Kolkata" };
function fmtDate(yyyymmdd) {
  if (!yyyymmdd) return "";
  const [y, m, d] = yyyymmdd.split("-");
  try {
    const dt = new Date(Date.UTC(+y, +m - 1, +d));
    return dt.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      ...IST_OPTS,
    });
  } catch {
    return yyyymmdd;
  }
}
const rupee = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// -----------------------------------------------------------------------------
// Ledger aggregation — collapses booking-level rows into a clean daily view.
//
// Groups by (date, agency) for staff scope (multiple agencies per day possible)
// and by (date) alone for agency scope (a single agency drawer). Each output
// row has: date, agency, qty, rate, amount, received, advance, pending.
//
// Pending / Advance come from the End-of-Day running balance already computed
// on the server; positive balance = pending (they owe), negative = advance.
// -----------------------------------------------------------------------------
export function aggregateLedgerRows(rows, isAgencyScope, sortOrder = "desc") {
  const bookingKind = isAgencyScope ? "delivery" : "earning";
  const buckets = new Map();
  // Iter79b: bank-statement running balance — track the LAST balance
  // seen inside each bucket (batch / manual entry). Since backend rows
  // arrive in chronological order with running balance already
  // computed by `_apply_running_balance`, the last row of each batch
  // holds that batch's post-transaction balance.
  const balByBucket = new Map();
  // Manual entries (payment / adjustment) are NOT aggregated — each one
  // stays its own row so the user can see every individual transaction
  // they posted (e.g. three separate payouts of ₹28,525, ₹20,000, ₹29,200
  // must show as three rows, not one merged ₹77,725). This mirrors how a
  // conventional bank/khata book keeps every debit-credit line distinct.
  const manualRows = [];

  for (const r of rows) {
    if (r.kind === "brought_forward") {
      const bfKey = `BF||${r.date}`;
      manualRows.push({
        date: r.date, agency: "Opening Balance (B/F)", qty: 0, rate: 0,
        amount: 0, received: 0, isManual: true, isBF: true, kind: r.kind, _bucketKey: bfKey,
      });
      balByBucket.set(bfKey, r.balance);
      continue;
    }
    if (
      r.kind === "payment" ||
      r.kind === "adjustment" ||
      r.kind === "adjustment_minus"
    ) {
      // Preserve each manual entry as its own line. Use `r.description`
      // (backend field name) or `r.note` fallback in the agency column so
      // the admin can see WHY the entry was posted.
      // Naming: payment = "Payout", adjustment = "Adjustment (+)",
      // adjustment_minus = "Adjustment (−)" — matches the dropdown labels
      // in Wallet.jsx exactly so the ledger reads back cleanly.
      const defaultLabel =
        r.kind === "payment"
          ? "Payout"
          : r.kind === "adjustment_minus"
          ? "Adjustment (−)"
          : "Adjustment (+)";
      const manualKey = `M||${r.id || `${r.date}-${r.kind}-${r.credit || r.debit}`}`;
      const manualBucket = {
        id: r.id, // wallet_entries doc id — needed for DELETE from UI
        date: r.date,
        agency: (r.description || r.note || "").trim() || defaultLabel,
        // Qty / Rate pulled through from backend `wallet_entries.qty|rate`
        // (saved when admin posts a manual booking adjustment with
        // qty×rate=amount). Legacy rows don't have these → render as 0.
        qty: Number(r.qty || 0),
        rate: Number(r.rate || 0),
        amount: Number(r.debit || 0),
        received: Number(r.credit || 0),
        isManual: true,
        kind: r.kind,
        _bucketKey: manualKey,
      };
      manualRows.push(manualBucket);
      if (r.balance !== undefined && r.balance !== null) {
        balByBucket.set(manualKey, r.balance);
      }
      continue;
    }
    if (r.kind !== bookingKind) continue; // ignore 'opening' — seed only
    // Iter79: bank-statement mode — bucket by (date + message_id) so each
    // upload is its own row. Two uploads on the same day = two rows
    // (was previously merged into one row per date). Fallback to
    // (date + time) if message_id missing for legacy rows.
    const batchKey = r.message_id || `${r.date}-${r.time || ""}`;
    const key = isAgencyScope
      ? `${r.date}||${batchKey}`
      : `${r.date}||${(r.agency || "—").trim() || "—"}||${batchKey}`;
    if (!buckets.has(key)) {
      buckets.set(key, {
        date: r.date,
        agency: isAgencyScope ? "" : (r.agency || "").trim(),
        qty: 0,
        rate: 0,
        amount: 0,
        received: 0,
        _bucketKey: key,
      });
    }
    const b = buckets.get(key);
    b.qty += 1;
    b.amount += Number(r.debit || 0);
    b.rate = Number(r.rate || b.rate || 0);
    // Track post-batch running balance (last entry in the batch wins).
    if (r.balance !== undefined && r.balance !== null) {
      balByBucket.set(key, r.balance);
    }
  }

  const combined = [...buckets.values(), ...manualRows].sort((a, b) => {
    // Iter80: user-controllable sort. "desc" (default) shows newest at top
    // matching the pre-toggle behaviour; "asc" flips to bank-passbook mode
    // (oldest at top, running balance flows downward).
    if (a.date !== b.date) {
      return sortOrder === "asc"
        ? a.date.localeCompare(b.date)
        : b.date.localeCompare(a.date);
    }
    // Within the same date: manual entries first (so the payout is visible
    // at the top of the date's block), then agency bookings alphabetically.
    if (!!a.isManual !== !!b.isManual) return a.isManual ? -1 : 1;
    return (a.agency || "").localeCompare(b.agency || "");
  });
  for (const g of combined) {
    // Rate fallback: derive from amount/qty when qty>0 and rate unknown.
    if (!g.rate && g.qty > 0 && g.amount) g.rate = g.amount / g.qty;
    g.amount = Math.round(g.amount * 100) / 100;
    g.received = Math.round(g.received * 100) / 100;

    // Iter79b: each row now carries its OWN post-transaction balance
    // (bank-statement style). Fallback to 0 for buckets that somehow
    // never received a balance (defensive — should not happen).
    const bal = balByBucket.has(g._bucketKey)
      ? balByBucket.get(g._bucketKey)
      : 0;
    g.pending = Math.max(bal, 0);
    g.advance = -Math.min(bal, 0);
    delete g._bucketKey;
  }
  return combined;
}

// -----------------------------------------------------------------------------
// CSV — clean aggregated columns only (no DAC / LPG ID / operator).
// -----------------------------------------------------------------------------
function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function buildAggregatedCSV({ ledger, isAgency, title, sortOrder = "desc" }) {
  const agg = aggregateLedgerRows(ledger.rows || [], isAgency, sortOrder);
  const lines = [];
  lines.push(csvEscape(title));
  lines.push(csvEscape(`Window: ${ledger.from_date} → ${ledger.to_date}`));
  lines.push("");
  const totals = ledger.totals || {};
  lines.push(
    ["Opening", isAgency ? "Deliveries" : "Earnings", "Adjustments", isAgency ? "Paid" : "Payouts", "Balance"]
      .map(csvEscape)
      .join(",")
  );
  lines.push(
    [
      totals.opening || 0,
      isAgency ? (totals.delivery_earnings || 0) : (totals.earnings || 0),
      totals.adjustments || 0,
      // Iter74b: CSV totals row now negates Paid/Payouts to match the
      // on-screen display convention (money leaving ledger = negative).
      -(isAgency ? (totals.payments_received || 0) : (totals.payouts || 0)),
      totals.balance || 0,
    ]
      .map(csvEscape)
      .join(",")
  );
  lines.push("");
  lines.push(
    ["Date", "Agency", "Quantity", "Rate", "Received", "Advance", "Pending"]
      .map(csvEscape)
      .join(",")
  );
  for (const r of agg) {
    lines.push(
      [
        r.date,
        r.agency || (isAgency ? (ledger.agency?.name || "") : ""),
        r.qty,
        r.rate || "",
        r.received || 0,
        r.advance != null ? r.advance : "",
        r.pending != null ? r.pending : "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  return lines.join("\r\n");
}

// Back-compat named exports — Wallet.jsx (admin) still imports these.
export function buildStaffLedgerCSV(ledger, sortOrder = "desc") {
  return buildAggregatedCSV({
    ledger,
    isAgency: false,
    title: `XYZ CHATTING — STAFF LEDGER · ${ledger.staff?.name || "Staff"}`,
    sortOrder,
  });
}
export function buildAgencyLedgerCSV(ledger, sortOrder = "desc") {
  return buildAggregatedCSV({
    ledger,
    isAgency: true,
    title: `XYZ CHATTING — AGENCY LEDGER · ${ledger.agency?.name || ""}`,
    sortOrder,
  });
}

export function downloadCSV(name, csv) {
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// -----------------------------------------------------------------------------
// Direct PDF download — no print dialog, no popup. jsPDF + autotable.
// Font support: rupee sign renders as "Rs." because default Helvetica has no
// glyph for ₹ (avoids random square boxes in the downloaded file).
// -----------------------------------------------------------------------------
export function downloadLedgerPDF({ title, subtitle, ledger, isAgency = false, filename, sortOrder = "desc" }) {
  const money = (n) =>
    "Rs. " +
    Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  const agg = aggregateLedgerRows(ledger.rows || [], isAgency, sortOrder);
  const totals = ledger.totals || {};
  const totalQty = agg.reduce((a, r) => a + (r.qty || 0), 0);
  const totalReceived = agg.reduce((a, r) => a + (r.received || 0), 0);
  // Iter81b: current outstanding = backend's authoritative
  // `ledger.totals.balance` — same source as the on-screen top card and
  // the AggregatedLedgerTable's `currentBalance` prop. Fixes a regression
  // where scanning `agg` picked the wrong row when a date had multiple
  // deliveries (e.g. 28 Sept had 3 deliveries + 1 payment, footer showed
  // the FIRST delivery's mid-day balance instead of the day's closing).
  const totalsBalance = Number(ledger.totals?.balance ?? 0);
  const currentPending = Math.max(totalsBalance, 0);
  const currentAdvance = -Math.min(totalsBalance, 0);

  const agencyFallback = isAgency ? (ledger.agency?.name || "") : "";
  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const marginX = 32;

  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(28, 28, 25);
  doc.text(title, marginX, 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(107, 107, 97);
  doc.text(subtitle, marginX, 62);

  // Totals strip — 5 side-by-side cards (Opening / Deliveries|Earnings /
  // Adjustments / Paid|Payouts / Balance).
  const cards = [
    ["Opening", totals.opening || 0],
    [isAgency ? "Deliveries" : "Earnings", isAgency ? (totals.delivery_earnings || 0) : (totals.earnings || 0)],
    ["Adjustments", totals.adjustments || 0],
    // Iter74: negate `payments_received`/`payouts` so PDF matches the
    // on-screen display convention (screen shows Paid as a negative
    // outflow like "Rs. -1,77,400"). Prior version showed positive.
    [isAgency ? "Paid" : "Payouts", -(isAgency ? (totals.payments_received || 0) : (totals.payouts || 0))],
    ["Balance", totals.balance || 0],
  ];
  const stripY = 80;
  const stripH = 44;
  const gap = 6;
  const cardW = (pageW - marginX * 2 - gap * 4) / 5;
  for (let i = 0; i < cards.length; i++) {
    const x = marginX + i * (cardW + gap);
    doc.setDrawColor(237, 237, 229);
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(x, stripY, cardW, stripH, 4, 4, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(107, 107, 97);
    doc.text(cards[i][0].toUpperCase(), x + 8, stripY + 14);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(28, 28, 25);
    doc.text(money(cards[i][1]), x + 8, stripY + 32);
  }

  // Table
  const bodyRows = agg.length === 0
    ? [[{ content: "No activity in this window.", colSpan: 7, styles: { halign: "center", textColor: [107, 107, 97] } }]]
    : agg.map((r) => [
        fmtDate(r.date),
        r.agency || agencyFallback,
        String(r.qty),
        r.rate ? money(r.rate) : "",
        r.received ? `-${money(r.received)}` : "",
        r.advance != null && r.advance > 0 ? money(r.advance) : "",
        r.pending != null && r.pending > 0 ? money(r.pending) : "",
      ]);
  if (agg.length > 0) {
    bodyRows.push([
      { content: "TOTAL / CURRENT", colSpan: 2, styles: { fontStyle: "bold", fillColor: [245, 245, 240] } },
      { content: String(totalQty), styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 240] } },
      { content: "", styles: { fillColor: [245, 245, 240] } },
      { content: totalReceived > 0 ? `-${money(totalReceived)}` : money(totalReceived), styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 240], textColor: [4, 120, 87] } },
      { content: currentAdvance > 0 ? money(currentAdvance) : "", styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 240], textColor: [30, 58, 138] } },
      { content: currentPending > 0 ? money(currentPending) : "", styles: { halign: "right", fontStyle: "bold", fillColor: [245, 245, 240], textColor: [153, 27, 27] } },
    ]);
  }
  autoTable(doc, {
    startY: stripY + stripH + 18,
    head: [["Date", "Agency", "Qty", "Rate", "Received", "Advance", "Pending"]],
    body: bodyRows,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 5,
      lineColor: [237, 237, 229],
      lineWidth: 0.4,
      textColor: [28, 28, 25],
    },
    headStyles: {
      fillColor: [245, 245, 240],
      textColor: [107, 107, 97],
      fontStyle: "bold",
      fontSize: 8,
      halign: "left",
    },
    columnStyles: {
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right", textColor: [4, 120, 87] },
      5: { halign: "right", textColor: [30, 58, 138] },
      6: { halign: "right", textColor: [153, 27, 27] },
    },
    margin: { left: marginX, right: marginX },
  });

  // Footer on every page
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(107, 107, 97);
    const stamp = `Generated ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST · XYZ Chatting · Page ${p}/${pageCount}`;
    doc.text(stamp, pageW / 2, doc.internal.pageSize.getHeight() - 18, { align: "center" });
  }

  doc.save(filename || `${title.replace(/[^A-Za-z0-9]+/g, "_")}.pdf`);
}

// Back-compat alias — legacy callers imported this name for the print flow.
export const openPrintableLedger = downloadLedgerPDF;

// -----------------------------------------------------------------------------
// Aggregated ledger table — shared JSX for Wallet drawer + MyLedger page.
// -----------------------------------------------------------------------------
export function AggregatedLedgerTable({
  rows,
  isAgency,
  agencyFallback = "",
  currentBalance = null,
  onDeleteManual = null, // optional (id, kind, amount, date) => Promise
  sortOrder = "desc",
}) {
  const agg = useMemo(
    () => aggregateLedgerRows(rows || [], isAgency, sortOrder),
    [rows, isAgency, sortOrder]
  );
  // Show/Hide delete controls on manual rows. Off by default so a random
  // touch on the row can't remove an entry — admin must first "arm" the
  // controls, then click the trash icon per row.
  const [deleteArmed, setDeleteArmed] = useState(false);
  // Current outstanding = latest EOD balance. `currentBalance` prop
  // (authoritative from backend totals) wins if provided. Otherwise scan
  // agg for the row with the max date+time — this is sort-order agnostic
  // (works whether user has toggled to ASC or DESC).
  const currentPendingAdvance = useMemo(() => {
    if (currentBalance != null) {
      return {
        pending: Math.max(currentBalance, 0),
        advance: -Math.min(currentBalance, 0),
      };
    }
    // Find row with the latest date; ties broken by manual-vs-booking
    // ordering (bookings after manuals, matching the intra-day sort).
    let best = null;
    for (const r of agg) {
      if (r.pending == null && r.advance == null) continue;
      if (
        !best ||
        r.date > best.date ||
        (r.date === best.date && !r.isManual && best.isManual)
      ) {
        best = r;
      }
    }
    return best
      ? { pending: best.pending || 0, advance: best.advance || 0 }
      : { pending: 0, advance: 0 };
  }, [agg, currentBalance]);
  const totals = useMemo(
    () =>
      agg.reduce(
        (a, r) => ({
          qty: a.qty + (r.qty || 0),
          received: a.received + (r.received || 0),
        }),
        { qty: 0, received: 0 }
      ),
    [agg]
  );
  return (
    <div className="bg-white border border-[#EDEDE5] rounded overflow-hidden">
      {onDeleteManual && (
        <div className="px-3 py-2 border-b border-[#EDEDE5] bg-[#F8F8F3] flex items-center justify-between gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#6B6B61]">
            Manual entries · click 🗑 to remove
          </span>
          <button
            type="button"
            onClick={() => setDeleteArmed((v) => !v)}
            data-testid="ledger-toggle-delete"
            className={`text-[10px] font-mono tracking-wider uppercase px-2 py-1 rounded border flex items-center gap-1 transition-colors ${
              deleteArmed
                ? "bg-[#B84F3D] text-white border-[#B84F3D]"
                : "bg-white text-[#6B6B61] border-[#DCDCD3] hover:border-[#6B6B61]"
            }`}
            title={deleteArmed ? "Hide delete controls" : "Show delete controls (admin only)"}
          >
            {deleteArmed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {deleteArmed ? "Hide delete" : "Show delete"}
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="text-[10px] uppercase tracking-wider text-[#6B6B61] bg-[#F5F5F0]">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Agency</th>
              <th className="text-right px-3 py-2">Quantity</th>
              <th className="text-right px-3 py-2">Rate</th>
              <th className="text-right px-3 py-2 text-emerald-700">Received</th>
              <th className="text-right px-3 py-2 text-blue-700">Advance</th>
              <th className="text-right px-3 py-2 text-red-700">Pending</th>
              {onDeleteManual && deleteArmed && (
                <th className="px-2 py-2 w-8" />
              )}
            </tr>
          </thead>
          <tbody>
            {agg.length === 0 && (
              <tr>
                <td colSpan={onDeleteManual && deleteArmed ? 8 : 7} className="text-center py-6 text-[#6B6B61]">
                  No activity in this window.
                </td>
              </tr>
            )}
            {agg.map((r, i) => (
              <tr
                key={`${r.date}-${r.agency || "_"}-${i}`}
                className={`border-t border-[#F0F0EA] hover:bg-[#F8F8F3] ${r.isBF ? "bg-[#FFF8E6] font-semibold" : ""}`}
                data-testid={r.isBF ? "ledger-bf-row" : `ledger-agg-row-${i}`}
              >
                <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">
                  {fmtDate(r.date)}
                </td>
                <td className="px-3 py-1.5 truncate max-w-[200px]" title={r.agency || agencyFallback}>
                  {r.agency || agencyFallback}
                </td>
                <td className="px-3 py-1.5 text-right font-mono font-semibold">
                  {r.isBF ? "" : r.qty}
                </td>
                <td className="px-3 py-1.5 text-right font-mono">
                  {r.rate ? rupee(r.rate) : ""}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-emerald-700">
                  {r.received ? `− ${rupee(r.received)}` : ""}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-blue-700 font-semibold">
                  {r.advance != null && r.advance > 0 ? rupee(r.advance) : ""}
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-red-700 font-semibold">
                  {r.pending != null && r.pending > 0 ? rupee(r.pending) : ""}
                </td>
                {onDeleteManual && deleteArmed && (
                  <td className="px-2 py-1.5 text-center">
                    {r.isManual && r.id ? (
                      <button
                        onClick={() => {
                          const label = r.agency || (r.kind === "payment" ? "Payout" : "Adjustment");
                          const amt = r.received || r.amount || 0;
                          if (
                            !window.confirm(
                              `Delete "${label}" on ${fmtDate(r.date)} (Rs. ${amt})?\n\nThis cannot be undone — the wallet entry will be permanently removed.`,
                            )
                          )
                            return;
                          onDeleteManual(r.id, r.kind, amt, r.date);
                        }}
                        data-testid={`ledger-agg-delete-${i}`}
                        className="text-[#B84F3D] hover:bg-[#B84F3D] hover:text-white p-1 rounded transition-colors"
                        title={`Delete this ${r.kind} entry`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span className="text-[#DCDCD3] text-xs" title="Booking rows cannot be deleted from this UI">
                        —
                      </span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          {agg.length > 0 && (
            <tfoot className="bg-[#F5F5F0] border-t-2 border-[#385C4B]" data-testid="ledger-agg-total">
              <tr>
                <td
                  className="px-3 py-2 font-bold uppercase tracking-wider text-xs"
                  colSpan={2}
                  title="Current outstanding is shown for Pending/Advance (not a sum of daily balances)."
                >
                  TOTAL / CURRENT
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold">{totals.qty}</td>
                <td className="px-3 py-2" />
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">
                  {totals.received > 0 ? `− ${rupee(totals.received)}` : rupee(totals.received)}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-blue-700">
                  {currentPendingAdvance.advance > 0 ? rupee(currentPendingAdvance.advance) : ""}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-red-700">
                  {currentPendingAdvance.pending > 0 ? rupee(currentPendingAdvance.pending) : ""}
                </td>
                {onDeleteManual && deleteArmed && <td />}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared date-range chips — Today / Yesterday / Last 7 days / Custom (picker).
// Emits `{ from, to }` in YYYY-MM-DD (IST) via `onChange`. Consumer decides
// when to refetch (typically on every change).
// -----------------------------------------------------------------------------
function _istISODate(offsetDays = 0) {
  const now = new Date();
  const ist = new Date(now.getTime() + (330 - now.getTimezoneOffset()) * 60000);
  ist.setDate(ist.getDate() - offsetDays);
  return ist.toISOString().slice(0, 10);
}

export function DateRangeChips({ from, to, onChange, testidPrefix = "date-chip" }) {
  const [showCustom, setShowCustom] = useState(false);
  const today = _istISODate(0);
  const y = _istISODate(1);
  const w = _istISODate(6);
  const presets = [
    { key: "today", label: "Today", from: today, to: today },
    { key: "yesterday", label: "Yesterday", from: y, to: y },
    { key: "7d", label: "Last 7 days", from: w, to: today },
    { key: "30d", label: "Last 30 days", from: _istISODate(29), to: today },
    { key: "all", label: "All time", from: "2020-01-01", to: today },
  ];
  const activeKey = presets.find((p) => p.from === from && p.to === to)?.key;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mb-3">
      {presets.map((p) => (
        <button
          key={p.key}
          onClick={() => {
            setShowCustom(false);
            onChange({ from: p.from, to: p.to });
          }}
          data-testid={`${testidPrefix}-${p.key}`}
          className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${
            activeKey === p.key
              ? "bg-[#385C4B] text-white border-[#385C4B]"
              : "bg-white text-[#385C4B] border-[#385C4B]/30 hover:bg-[#385C4B]/10"
          }`}
        >
          {p.label}
        </button>
      ))}
      <button
        onClick={() => setShowCustom((s) => !s)}
        data-testid={`${testidPrefix}-custom`}
        className={`px-2.5 py-1 text-xs font-semibold rounded border transition-colors ${
          showCustom || !activeKey
            ? "bg-[#8A6046] text-white border-[#8A6046]"
            : "bg-white text-[#8A6046] border-[#8A6046]/30 hover:bg-[#8A6046]/10"
        }`}
      >
        Custom…
      </button>
      {(showCustom || !activeKey) && (
        <div className="flex items-center gap-1.5 ml-1" data-testid={`${testidPrefix}-custom-panel`}>
          <input
            type="date"
            value={from}
            max={to}
            onChange={(e) => onChange({ from: e.target.value, to })}
            data-testid={`${testidPrefix}-from`}
            className="px-2 py-1 text-xs border border-[#DCDCD3] rounded"
          />
          <span className="text-xs text-[#6B6B61]">→</span>
          <input
            type="date"
            value={to}
            min={from}
            max={today}
            onChange={(e) => onChange({ from, to: e.target.value })}
            data-testid={`${testidPrefix}-to`}
            className="px-2 py-1 text-xs border border-[#DCDCD3] rounded"
          />
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Team-staff facing page — clean aggregated view only.
// -----------------------------------------------------------------------------
export default function MyLedger() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ from: "2020-01-01", to: _istISODate(0) });
  const [sortOrder, setSortOrder] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/wallet/ledger/staff/me?from_=${range.from}&to=${range.to}`
      );
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  return (
    <div className="min-h-screen bg-[#F8F8F3]">
      <header className="border-b border-[#EDEDE5] bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#385C4B]" />
            <h1
              className="font-semibold text-lg tracking-tight text-[#1C1C19]"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              My Ledger
            </h1>
            <span className="text-xs uppercase tracking-wider bg-[#385C4B] text-white px-2 py-0.5 rounded-full">
              Team Staff
            </span>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-sm font-medium text-[#1C1C19]">
                {user?.display_name}
              </div>
              <div className="text-[10px] text-[#6B6B61] font-mono">
                {user?.staff_name || user?.username}
              </div>
            </div>
            <EkycChatButton className="border border-[#2F6F8F] text-[#2F6F8F] hover:bg-[#2F6F8F]/5" />
            <button
              onClick={logout}
              className="w-9 h-9 rounded flex items-center justify-center hover:bg-[#F0F0EA]"
              data-testid="my-ledger-logout"
              title="Logout"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Team-staff can also see their linked resend teams' board. Show a
            prominent link so leads jump between "my earnings" and "team
            resend activity" easily. */}
        {(user?.linked_resend_teams || []).length > 0 && (
          <div className="max-w-4xl mx-auto px-4 pb-3">
            <a
              href="/resend-only"
              data-testid="my-ledger-goto-resend"
              className="inline-flex items-center gap-2 text-xs font-semibold text-[#385C4B] hover:text-[#2D4A3C] border border-[#385C4B]/40 hover:border-[#385C4B] rounded px-3 py-1.5 transition-colors"
            >
              → Team Resend Desk ({(user.linked_resend_teams || []).join(", ")})
            </a>
          </div>
        )}
      </header>

      <main className="max-w-4xl mx-auto p-4">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-[#385C4B]" />
          </div>
        )}
        {data && !loading && (
          <>
            <DateRangeChips
              from={range.from}
              to={range.to}
              onChange={setRange}
              testidPrefix="my-ledger-date"
            />
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-4">
              {[
                ["Opening", data.totals.opening],
                ["Earnings", data.totals.earnings],
                // Iter74b: negate on-screen Payouts to match admin +
                // PDF convention (money leaving ledger shows negative).
                ["Payouts", -(data.totals.payouts || 0)],
                ["Balance", data.totals.balance],
              ].map(([label, val]) => (
                <div
                  key={label}
                  className="bg-white border border-[#EDEDE5] rounded px-3 py-2"
                  data-testid={`my-ledger-total-${label.toLowerCase()}`}
                >
                  <div className="text-[10px] uppercase tracking-wider text-[#6B6B61]">
                    {label}
                  </div>
                  <div
                    className={`font-mono font-semibold text-base ${
                      label === "Balance"
                        ? val > 0
                          ? "text-emerald-700"
                          : val < 0
                          ? "text-red-700"
                          : ""
                        : ""
                    }`}
                  >
                    {rupee(val)}
                  </div>
                </div>
              ))}
            </div>

            {/* Download bar */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                type="button"
                onClick={() =>
                  setSortOrder((v) => (v === "desc" ? "asc" : "desc"))
                }
                data-testid="my-ledger-sort-toggle"
                title={
                  sortOrder === "desc"
                    ? "Abhi Newest first — click for bank passbook mode"
                    : "Abhi Oldest first (passbook) — click for Newest first"
                }
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 6L4 10M4 10L8 14M4 10H16M16 18L20 14M20 14L16 10M20 14H8" /></svg>
                Sort: {sortOrder === "desc" ? "Newest first" : "Oldest first"}
              </button>
              <button
                onClick={async (ev) => {
                  // Iter74: fresh-fetch before CSV — file must match on-screen range/totals.
                  const btn = ev.currentTarget;
                  btn.setAttribute("data-loading", "1");
                  try {
                    const { data: fresh } = await api.get(
                      `/wallet/ledger/staff/me?from_=${range.from}&to=${range.to}`
                    );
                    setData(fresh);
                    downloadCSV(
                      `MyLedger_${fresh.staff.name.replace(/\s+/g, "_")}_${fresh.to_date}.csv`,
                      buildStaffLedgerCSV(fresh, sortOrder)
                    );
                  } catch (e) {
                    toast.error(formatApiError(e) || "CSV download failed");
                  } finally {
                    btn.removeAttribute("data-loading");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                data-testid="my-ledger-download-csv"
              >
                <Download className="w-3.5 h-3.5" /> Download Excel (CSV)
              </button>
              <button
                onClick={async (ev) => {
                  // Iter74: fresh-fetch before PDF.
                  const btn = ev.currentTarget;
                  btn.setAttribute("data-loading", "1");
                  try {
                    const { data: fresh } = await api.get(
                      `/wallet/ledger/staff/me?from_=${range.from}&to=${range.to}`
                    );
                    setData(fresh);
                    downloadLedgerPDF({
                      title: `Staff Ledger — ${fresh.staff.name}`,
                      subtitle: `Window ${fresh.from_date} to ${fresh.to_date} · Generated ${new Date().toLocaleDateString("en-IN")}`,
                      ledger: fresh,
                      isAgency: false,
                      filename: `MyLedger_${fresh.staff.name.replace(/\s+/g, "_")}_${fresh.to_date}.pdf`,
                      sortOrder,
                    });
                  } catch (e) {
                    toast.error(formatApiError(e) || "PDF download failed");
                  } finally {
                    btn.removeAttribute("data-loading");
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-[#385C4B]/40 text-[#385C4B] rounded hover:bg-[#385C4B] hover:text-white transition-colors data-[loading=1]:opacity-60"
                data-testid="my-ledger-download-pdf"
              >
                <FileDown className="w-3.5 h-3.5" /> Download PDF
              </button>
              <span className="text-[11px] text-[#6B6B61] ml-auto">
                {data.rows.length} entries in window
              </span>
            </div>

            {/* Aggregated daily table — no DAC / LPG-ID clutter */}
            <AggregatedLedgerTable rows={data.rows} isAgency={false} sortOrder={sortOrder} currentBalance={data.totals?.balance ?? null} />
          </>
        )}
      </main>
    </div>
  );
}
